/**
 * Notification idempotency keys.
 *
 * A logical event for one recipient has exactly ONE key. The server
 * (`notify-quote`) writes it into `notifications.event_key`, which carries a
 * unique index, so a double-click / retry / realtime echo cannot create a second
 * notification. Different events — and different stages of the same entity —
 * produce different keys and therefore remain separate notifications.
 *
 * This module mirrors the server helper so the rule is unit-testable.
 */
export function notificationEventKey(parts: Array<string | number | null | undefined>): string {
  return parts.map((p) => String(p ?? '')).join(':');
}

export const notificationEventKeys = {
  quoteCreated: (quoteId: string, recipientId: string) =>
    notificationEventKey(['quote_created', quoteId, recipientId]),
  quoteUpdated: (quoteId: string, status: string, recipientId: string) =>
    notificationEventKey(['quote_updated', quoteId, status, recipientId]),
  poCreated: (poId: string, recipientId: string) =>
    notificationEventKey(['po_created', poId, recipientId]),
  poUpdated: (poId: string, status: string, recipientId: string) =>
    notificationEventKey(['po_updated', poId, status, recipientId]),
};

/**
 * In-flight guard for client-triggered notification calls: a double-clicked
 * button cannot fire the same event twice within the same page session. The
 * durable guarantee remains the server-side unique key; this only avoids the
 * needless duplicate round trip.
 */
const inFlight = new Set<string>();

export async function runOnceInFlight(key: string, fn: () => Promise<void>): Promise<void> {
  if (inFlight.has(key)) return;
  inFlight.add(key);
  try {
    await fn();
  } finally {
    inFlight.delete(key);
  }
}
